import React, { useState, useRef, useEffect } from "react";
import { Favourites, FlightRequestByIdResponse, MyRequestItem, MyRequestResponse } from "./types";
import {
  addFavourites,
  deleteFavourites,
  enableUpdateRequests,
  getFavourites,
  getLoadRequestDetailsByID,
  pinUnpinRequests,
  validateFavouriteFlight,
} from "apis/myRequestsService";
import { getAirlineNotesById, AirlineNotesResponse } from "apis/airlineNotesService";
import { getTimeAgo } from "utils/dateUtils";
import RequestUpdateModal from "./components/RequestUpdateModal";
import DeleteRequestModal from "./components/DeleteRequestModal";
import { useToastStore } from "store/toastStore";
import ShareRequestModal from "./components/ShareRequestModal";
import ReportInaccurateLoadPopup from "./components/ReportInaccurateLoadPopup";
import DefaultAirLogo from "../../assets/images/logo.svg";
import AirlineNotesModal from "components/AirlineNotesModal";
import { AirlineNote, AirlineNoteSections } from "types/airlineNotes";
import { Loader2 } from "lucide-react";
import { useGlobalLoader } from "../../../src/contexts/GlobalLoaderContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

interface Flight {
  id: number;
  isPinned?: boolean; // Optional since bulk updates may not require this
}

interface FlightListingProps {
  flights: MyRequestResponse;
  action: string | null;
  clearSelectAll: boolean;
  fetchLoadRequests: () => void;
  isFavouritesPage?: boolean;
  loading?: boolean; // show loader while requests are being fetched
}

const transformNotesResponse = (data: AirlineNotesResponse, fallbackName: string): AirlineNote => {
  const sections: AirlineNoteSections = {};

  if (data.notes) {
    Object.entries(data.notes).forEach(([key, rawValue]) => {
      if (rawValue === null || rawValue === undefined) return;

      if (Array.isArray(rawValue)) {
        const cleanedItems = rawValue
          .map((item) => (typeof item === "string" ? item.trim() : ""))
          .filter((item): item is string => item.length > 0);

        if (cleanedItems.length > 0) {
          sections[key] = cleanedItems;
        }
        return;
      }

      if (typeof rawValue === "string") {
        const trimmed = rawValue.trim();
        if (trimmed.length > 0) {
          sections[key] = trimmed;
        }
      }
    });
  }

  return {
    id: data.id,
    airlineId: data.airlineId,
    airlineName: data.airlineName || fallbackName,
    sections,
    lastUpdated: data.createdAt,
    createdAt: data.createdAt,
  };
};

const FavouriteListing: React.FC<FlightListingProps> = ({
  flights,
  action,
  clearSelectAll,
  fetchLoadRequests,
  isFavouritesPage,
  loading,
}) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [openAccordion, setOpenAccordion] = useState<Set<number>>(new Set());
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [selectedFlights, setSelectedFlights] = useState<string[]>([]);
  const [isRequestUpdateOpen, setIsRequestUpdateOpen] = useState(false);
  const [currentFlight, setCurrentFlight] = useState<any>(null);
  const [isBulkOperation, setIsBulkOperation] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);
  const [selectedFlightId, setSelectedFlightId] = useState<number | null>(null);
  const [isShareRequestModalOpen, setShareRequestModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState(""); // Store generated URL
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [postDate, setPostDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [selectedAirlineNote, setSelectedAirlineNote] = useState<AirlineNote | null>(null);
  const [notesCache, setNotesCache] = useState<Record<number, AirlineNote>>({});
  const [notesLoadingFlightId, setNotesLoadingFlightId] = useState<number | null>(null);
  const [favourites, setFavourites] = useState<Favourites[]>([]);
  const [localFlights, setLocalFlights] = useState<MyRequestItem[]>([]);
  const [postRequestLoader, setPostRequestLoader] = useState<boolean>(false);

  const { showLoader, hideLoader } = useGlobalLoader();
  const isFlightSelected = (flightId: number) => selectedFlights.includes(flightId.toString());

  useEffect(() => {
    if (clearSelectAll) {
      setSelectedFlights([]); // Clear selections when page changes
    }
  }, [clearSelectAll]);

  useEffect(() => {
    const initialOpen = new Set<number>();
    flights?.items?.forEach((flight: any) => {
      if (flight?.status === "Answered" || flight?.status === "Update Requested") {
        initialOpen.add(flight.id);
      }
    });
    setOpenAccordion(initialOpen);
  }, [flights]);

  // In single-select mode: use handleSelectFlight to select/deselect a single flight

  //   useEffect(() => {
  //     fetchFavourites();
  //   }, []);

  const handleSelectFlight = (flightId: string) => {
    const isSelected = selectedFlights.includes(flightId);
    if (isSelected) {
      // Deselect current selection
      setSelectedFlights([]);
    } else {
      // Select only this flight (single-select)
      setSelectedFlights([flightId]);
    }
  };

  const toggleFavourite = async (flight: FlightRequestByIdResponse) => {

    // setLocalFlights((prev) => prev.map((f) => (f.id === flight.id ? { ...f, isFavourite: !f.isFavourite } : f)));

    try {
      //   if (flight.isFavourite) {
      await deleteFavourites(flight.id);
      //   } else {
      //     const favouritesData = {
      //       airlineId: flight.airlineId,
      //       flightNumber: flight.flightNo,
      //       sourceCode: flight.sourceCode,
      //       destinationCode: flight.destinationCode,
      //       deptim: flight.deptim,
      //       arrtim: flight.arrtim,
      //       depterm: flight.departureTerminal,
      //       arrterm: flight.arrivalTerminal,
      //       elptim: flight.elptim,
      //       depcity: flight.depcity,
      //       arrcity: flight.arrcity,
      //       duration: flight.duration,
      //       originalDate: flight.departure,
      //       label: "Favourites",
      //     };

      //     await addFavourites(favouritesData);
      //     useToastStore.getState().showToast("Added to favourites successfully", "success", 3000);
      //   }

      // background refresh
      fetchLoadRequests();
      useToastStore.getState().showToast("Removed from favourites successfully", "success", 3000);
    } catch (error) {
      useToastStore.getState().showToast("Error updating favourites", "error", 3000);
    } 
  };

  // Convert selected flight IDs to numbers for the API
  const getSelectedRequestIds = (): number[] => {
    return selectedFlights.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id));
  };

  // Update select all state when flights data changes
  useEffect(() => {
    const totalFlights = flights?.items?.length || 0;
    if (totalFlights === 0) {
      setSelectedFlights([]);
    } else {
      // Ensure current selection still exists in the updated list
      setSelectedFlights((prev) => prev.filter((id) => flights?.items?.some((f: any) => f.id.toString() === id)));
    }
  }, [flights?.items?.length]);

  useEffect(() => {
    if (action && selectedFlights.length === 0) {
      useToastStore.getState().showToast("No Requests selected. Operation aborted.", "error", 3000);
      return; // Stop execution
    }

    setIsBulkOperation(true);
    setCurrentFlight(null);

    if (action === "requestUpdate") {
      setIsRequestUpdateOpen(true);
    } else if (action === "deleteRequest") {
      setIsDeleteModalOpen(true);
    } else if (action === "enableStatusUpdate") {
      console.log(selectedFlights); // Logs an array of string IDs
      const numericFlightIds = selectedFlights.map((id) => Number(id)); // Convert to numbers
      const handleStatusUpdate = async () => {
        try {
          const response = await enableUpdateRequests(numericFlightIds); // Pass converted array
          useToastStore.getState().showToast("Status Update successful", "success", 3000);
        } catch (error) {
          useToastStore.getState().showToast("Error updating status", "error", 3000);
        }
      };

      handleStatusUpdate(); // Call the async function
    } else if (action === "pinFlight") {
      const mappedFlights: Flight[] = selectedFlights.map((id) => ({
        id: Number(id),
      }));

      handlePinUnPin(mappedFlights, true);
    }
  }, [action]);

  // Open single flight update modal
  const handleSingleUpdate = (flight: any) => {
    setIsBulkOperation(false);
    setCurrentFlight(flight);
    console.log(flight.id);
    setIsRequestUpdateOpen(true);
  };

  const handleSingleDelete = (flight: any) => {
    setIsBulkOperation(false);
    setCurrentFlight(flight);
    setIsDeleteModalOpen(true);
  };

  const handleSingleStatusUpdate = async (flight: any) => {
    try {
      const response = await enableUpdateRequests([flight?.id]); // Pass as an array
      useToastStore.getState().showToast("Status Update successful", "success", 3000);
    } catch (error) {
      useToastStore.getState().showToast("Error updating status", "error", 3000);
    }
  };

  const handlePinUnPin = async (flights: Flight | Flight[], isBulk = false): Promise<void> => {
    try {
      const requestIds = isBulk ? (flights as Flight[]).map((flight) => flight.id) : [(flights as Flight).id];

      console.log(requestIds);

      const isPinned = isBulk ? true : !(flights as Flight).isPinned;

      const response = await pinUnpinRequests(requestIds, isPinned);

      console.log("Pin/Unpin Success:", response.data);

      const message = isPinned ? "Pinned successfully" : "Unpinned successfully";
      useToastStore.getState().showToast(message, "success", 3000);
      fetchLoadRequests();
    } catch (error) {
      console.error("Error in pin/unpin:", error);
      useToastStore.getState().showToast("Failed to update status", "error", 3000);
    }
  };

  const toggleAccordion = (id: number): void => {
    console.log(flights);
    const updated = new Set(openAccordion);
    if (updated.has(id)) {
      updated.delete(id); // Hide if already open
    } else {
      updated.add(id); // Show if closed
    }
    setOpenAccordion(updated);
  };

  const toggleDropdown = (id: number): void => {
    setOpenDropdown(openDropdown === id ? null : id);
  };

  const handleOpenNotes = async (flight: MyRequestItem, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    const airlineId = flight?.airlineId;
    const flightId = flight?.id;

    if (!airlineId) {
      useToastStore.getState().showToast("Airline information not available for this request.", "error", 3000);
      return;
    }

    const cached = notesCache[airlineId];
    if (cached) {
      setSelectedAirlineNote(cached);
      setIsNotesModalOpen(true);
      return;
    }

    try {
      setNotesLoadingFlightId(flightId ?? null);
      const response = await getAirlineNotesById(airlineId);
      const transformed = transformNotesResponse(response, flight?.airlineName || "Unknown");
      setNotesCache((prev) => ({ ...prev, [airlineId]: transformed }));
      setSelectedAirlineNote(transformed);
      setIsNotesModalOpen(true);
    } catch (error) {
      console.error("Error fetching airline notes:", error);
      useToastStore.getState().showToast("This airline has no custom notes available yet", "error", 3000);
    } finally {
      setNotesLoadingFlightId(null);
    }
  };

  const handleCloseNotes = () => {
    setIsNotesModalOpen(false);
    setSelectedAirlineNote(null);
  };

  useEffect(() => {
    console.log("data-child:", flights);
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const openPostModal = () => {
    if (selectedFlights.length === 0) {
      useToastStore.getState().showToast("Please select at least one flight.", "error", 3000);
      return;
    }
    setIsPostModalOpen(true);
  };

  return (
    <section className="mt-6">
      {loading ? (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-50 animate-in fade-in duration-200"
          style={{ zIndex: 9999 }} // Force top layer
        >
          <div className="bg-white/90 backdrop-blur rounded-xl p-8 shadow-2xl flex flex-col items-center gap-4 animate-in zoom-in-50 duration-200">
            <Loader2 className="w-12 h-12 text-[#FFD700] animate-spin" />
            <p className="text-gray-800 font-semibold text-lg">Loading</p>
          </div>
        </div>
      ) : flights.items.length === 0 ? (
        <div className="flex justify-center items-center h-32 w-full text-gray-500 mt-24">
          <p>{isFavouritesPage ? "No favourites found" : "No flight requests found."}</p>
        </div>
      ) : (
        flights.items.map((flight: any) => (
          <div
            key={flight.id}
            className={`mt-6 cursor-pointer transition-all ${
              isFlightSelected(flight.id) ? "border-2 border-[#FFD700] shadow-lg rounded-lg" : ""
            }`}
            id={`accordion-collapse-${flight.id}`}
            onClick={() => handleSelectFlight(flight.id.toString())}
            data-accordion="collapse"
          >
            <div className={`bg-white dark:bg-white p-5 rounded-lg w-full`}>
              <div className="flex items-center">
                {/* Individual Checkbox with Flight ID */}
                <input
                  type="checkbox"
                  value={flight.id.toString()}
                  //   checked={selectedFlights.includes(flight.id.toString())}
                  checked={isFlightSelected(flight.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => handleSelectFlight(flight.id.toString())}
                  //   onChange={() => handleSelectFlight(flight.id.toString())}
                  //   onClick={(e) => e.stopPropagation()}
                  className="mr-3 w-4 h-4 cursor-pointer"
                />

                <button
                  onClick={() => {
                    if (isFavouritesPage) {
                      handleSelectFlight(flight.id.toString());
                    } else {
                      toggleAccordion(flight?.id);
                    }
                  }}
                  className="w-full"
                  aria-expanded={openAccordion.has(flight?.id)}
                >
                  <div className="grid grid-cols-12 gap-6 items-center justify-between">
                    <div className="flex items-center lg:col-span-2 sm:col-span-4 col-span-12 gap-3">
                      <img
                        className="w-11"
                        src={flight.airlineLogo || DefaultAirLogo}
                        alt={"Airline"}
                        onError={(e) => (e.currentTarget.src = "/assets/logos/air-logo.png")}
                      />
                      <div className="text-left">
                        <p className="text-lightgray-200 text-xs">Airlines</p>
                        <p className="text-grease-100 mt-1 text-sm font-medium">{flight?.airlineName}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center lg:col-span-6 sm:col-span-7 col-span-12 sm:justify-center gap-6">
                      <div>
                        <p className="text-lightgray-200 text-xs whitespace-nowrap">Flight No.</p>
                        <p className="text-grease-100 mt-1 text-sm font-medium">{flight?.flightNo}</p>
                      </div>
                      <div>
                        <p className="text-lightgray-200 text-xs ">Date</p>
                        <p className="text-grease-100 mt-1 text-sm font-medium w-[180px] text-center">
                          {flight?.departure
                            ? (() => {
                                const date = new Date(flight.departure);
                                const month = date.toLocaleDateString("en-US", {
                                  month: "long",
                                });
                                const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
                                const day = String(date.getDate()).padStart(2, "0"); // 👈 Add leading zero
                                const year = date.getFullYear();
                                const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
                                const capitalizedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1).toLowerCase();
                                return `${capitalizedMonth} ${day} ${year} - ${capitalizedWeekday}`;
                              })()
                            : ""}
                        </p>
                      </div>
                      <div className="flex flex-col items-center justify-center">
                        <button
                          onClick={(e) => handleOpenNotes(flight, e)}
                          className={`p-3 rounded-lg transition group border border-[#FFBC00] border-opacity-30 ${
                            notesLoadingFlightId === flight?.id ? "bg-yellow-50 cursor-not-allowed" : "hover:bg-yellow-50"
                          }`}
                          aria-label="View airline notes"
                          title="View airline notes"
                          disabled={notesLoadingFlightId === flight?.id}
                        >
                          {notesLoadingFlightId === flight?.id ? (
                            <svg
                              className="w-5 h-5 text-[#FFBC00] animate-spin"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                            </svg>
                          ) : (
                            <i className="st-info text-[#FFBC00] text-lg group-hover:text-yellow-600"></i>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center lg:col-span-4 col-span-12 gap-3 justify-end">
                      <div className="flex flex-col gap-1">
                        <p className="text-grease-100 text-sm font-medium">{flight?.sourceCode}</p>
                        <div className="flex items-center justify-center gap-1 w-fit mt-0.5">
                          <div className="rounded-full w-4 h-4 pr-0.5 bg-gray-500 flex items-center justify-center">
                            <i className="st-top-right text-white text-0.5 font-medium"></i>
                          </div>
                          <p className="text-lightgray-200 text-xs">
                            {/* {flight?.departure
                              ? new Date(flight.departure).toLocaleTimeString("en-GB", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: false, // Ensures 24-hour format
                                })
                              : ""} */}
                              {flight?.departure
                              ? new Date(flight.departure).toLocaleTimeString(
                                  "en-GB",
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: false,
                                    timeZone: "UTC", // 👈 prevents conversion
                                  }
                                )
                              : ""}
                          </p>
                        </div>
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0">
                            <div className="w-2 h-2 bg-white rounded-full flex items-center justify-center">
                              <div className="w-1 h-1 bg-[#FFD700] rounded-full"></div>
                            </div>
                          </div>
                          <div className="w-full border-b-2 border-dashed border-indigo-200"></div>
                          <i className="st-flight-slim text-[#FFD700] text-base"></i>
                          <div className="w-full border-b-2 border-dashed border-indigo-200"></div>
                          <div className="w-3 h-3 bg-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0">
                            <div className="w-2 h-2 bg-white rounded-full flex items-center justify-center">
                              <div className="w-1 h-1 bg-[#FFD700] rounded-full"></div>
                            </div>
                          </div>
                        </div>
                        <p className="text-lightgray-150 text-xs text-center mt-1">
                          {/* {flight?.duration && (
                            <span>
                              {(() => {
                                const [hours, minutes] = flight.duration.split(":");
                                return `${hours}h ${minutes}m`;
                              })()}
                            </span>
                          )} */}
                          {flight?.elptim && (
                            <span>
                              {flight.elptim.includes(":")
                                ? `${flight.elptim.split(":")[0]}h ${flight.elptim.split(":")[1]}m`
                                : `${parseInt(flight.elptim.slice(0, -2))}h ${parseInt(
                                    flight.elptim.slice(-2)
                                  )}m`}
                            </span>
                          )}
                        </p>
                      </div>

                      <div className="flex flex-col items-end gap-1">
                        <p className="text-grease-100 text-sm font-medium">{flight.destinationCode}</p>
                        <div className="flex items-center justify-center gap-1 w-fit mt-0.5">
                          <div className="rounded-full w-4 h-4 pl-0.5 bg-gray-500 flex items-center justify-center">
                            <i className="st-bottom-right text-white text-0.5 font-medium"></i>
                          </div>
                          <p className="text-lightgray-200 text-xs">
                            {/* {flight?.arrival
                              ? new Date(flight.arrival).toLocaleTimeString("en-GB", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: false, // Ensures 24-hour format
                                })
                              : ""} */}
                              {flight?.arrival
                              ? new Date(flight.arrival).toLocaleTimeString(
                                  "en-GB",
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: false,
                                    timeZone: "UTC", // 👈 IMPORTANT
                                  }
                                )
                              : ""}
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent event from propagating to other elements
                          toggleDropdown(flight.id); // Toggle dropdown visibility for the corresponding flight
                        }}
                        className="flex items-center gap-2 p-3 ml-3 bg-offwhite-50 rounded-lg"
                      >
                        <i className="st-more text-xs text-lightgray-200 rotate-90"></i>
                      </button>

                      {/* Favourite Icon */}
                      <div
                        className="cursor-pointer mr-2 hover:scale-110 transition"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavourite(flight);
                        }}
                      >
                        {/* {flight?.isFavourite ? ( */}
                        {/* // ✅ Filled Heart SVG */}
                        <svg
                          width="18"
                          height="17"
                          viewBox="0 0 18 17"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="text-red-500"
                        >
                          <path
                            d="M12.3799 0.149414C14.8323 0.149494 16.76 2.1358 17.0117 4.87012L17.0322 5.06055C17.0509 5.34293 17.0462 5.8953 16.8623 6.67773C16.5199 8.15991 15.703 9.52545 14.5283 10.5869L14.5273 10.5879L8.64844 15.834L8.54785 15.9238L8.44824 15.833L2.66211 10.5869C1.48739 9.52538 0.670474 8.15988 0.328125 6.67773C0.0876614 5.67947 0.152851 5.03447 0.176758 4.88086C0.423822 2.1401 2.37313 0.149415 4.82715 0.149414C6.34973 0.149414 7.71113 0.918999 8.56641 2.21582C9.41377 0.949865 10.8432 0.149414 12.3799 0.149414Z"
                            fill="#FF0000"
                            stroke="#FF0000"
                            strokeWidth="0.3"
                          />
                        </svg>
                        {/* ) : (
                          // ✅ Outline Heart SVG
                          <svg
                            width="18"
                            height="17"
                            viewBox="0 0 18 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="text-lightgray-150"
                          >
                            <path
                              d="M12.3799 0.149414C14.8323 0.149494 16.76 2.1358 17.0117 4.87012L17.0322 5.06055C17.0509 5.34293 17.0462 5.8953 16.8623 6.67773C16.5199 8.15991 15.703 9.52545 14.5283 10.5869L14.5273 10.5879L8.64844 15.834L8.54785 15.9238L8.44824 15.833L2.66211 10.5869C1.48739 9.52538 0.670474 8.15988 0.328125 6.67773C0.0876615 5.67947 0.152851 5.03447 0.176758 4.88086C0.423822 2.1401 2.37313 0.149415 4.82715 0.149414C6.34973 0.149414 7.71113 0.918999 8.56641 2.21582C9.41377 0.949865 10.8432 0.149414 12.3799 0.149414ZM12.3809 1.06348C10.9 1.06348 9.57619 1.97344 8.98438 3.37207L8.9834 3.37305L8.68555 4.06152L8.54785 4.37891L8.41016 4.06152L8.1123 3.37305V3.37207C7.50155 1.93423 6.23117 1.0636 4.80859 1.06348C2.87685 1.06348 1.30723 2.69661 1.10742 4.96387V5L1.10547 5.0127C1.1054 5.01314 1.10469 5.01427 1.10449 5.01562C1.10404 5.01865 1.10343 5.02412 1.10254 5.03125C1.10076 5.04551 1.09896 5.06832 1.09668 5.09863C1.09212 5.15934 1.08748 5.2517 1.09082 5.37402C1.09585 5.55777 1.1175 5.80941 1.17773 6.12305L1.25195 6.45801L1.25293 6.45898H1.25195C1.5607 7.74926 2.2704 8.93277 3.30957 9.88086L8.54883 14.6455L13.8984 9.88086C14.9366 8.95151 15.6453 7.76836 15.9541 6.45996C16.0634 5.98697 16.0951 5.6208 16.0996 5.37305C16.1019 5.24919 16.0977 5.1546 16.0928 5.09082C16.0903 5.05896 16.0879 5.03443 16.0859 5.01758C16.085 5.00905 16.0837 5.00262 16.083 4.99707C16.0827 4.99456 16.0823 4.99102 16.082 4.98828V4.98242L16.0586 4.76855C15.7837 2.57934 14.2521 1.0637 12.3809 1.06348Z"
                              fill="currentColor"
                              stroke="currentColor"
                              strokeWidth="0.3"
                            />
                          </svg>
                        )} */}
                      </div>

                      {/* Pin Icon */}
                      <div
                        className="cursor-pointer mr-2 hover:opacity-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Add your pin toggle function here, for example:
                          // togglePinStatus(flight.id);
                        }}
                      >
                        {flight.isPinned ? (
                          <i className="st-thumbpin text-[#FFD700] text-sm"></i>
                        ) : (
                          <i className="st-thumbpin text-lightgray-150 text-sm opacity-0 hover:opacity-50"></i>
                        )}
                      </div>

                      {/* Conditional rendering of dropdown for the first button */}
                      {openDropdown === flight.id && (
                        <div
                          ref={dropdownRef}
                          className="absolute z-10 bg-white divide-y divide-gray-100 rounded-lg  w-60 shadow-2xl py-2 border "
                          style={{
                            marginTop: "115px", // Move dropdown down from the button
                            right: "120px", // Move dropdown left from the right edge
                          }}
                        >
                          <ul className="px-4 text-md text-gray-700">
                            <li>
                              <a
                                href="#"
                                className="flex items-center gap-2 py-2 mt-2"
                                onClick={(e) => {
                                  e.preventDefault(); // Prevents page refresh
                                  e.stopPropagation(); // Prevents event propagation to the accordion
                                  // Set the current flight before opening the modal
                                  handlePinUnPin(flight);
                                }}
                              >
                                <i className="st-thumbpin text-lightgray-150 text-md"></i>
                                <p className="text-lightgray-200 text-md">{flight?.isPinned ? "Unpin Flight" : "Pin Flight"}</p>
                              </a>
                            </li>
                            {(flight.status === "Answered" || flight.status === "Update Requested") && (
                              <li>
                                <a
                                  href="#"
                                  className="flex items-center gap-2 py-2 mt-2"
                                  onClick={(e) => {
                                    e.preventDefault(); // Prevents page refresh
                                    e.stopPropagation(); // Prevents event propagation to the accordion
                                    // Set the current flight before opening the modal
                                    handleSingleUpdate(flight);
                                  }}
                                >
                                  <i className="st-reload text-lightgray-150 text-md"></i>
                                  <p className="text-lightgray-200 text-md">Request Update</p>
                                </a>
                              </li>
                            )}
                            {flight.status === "Answered" && (
                              <li>
                                <a
                                  href="#"
                                  className="flex items-center gap-2 py-2 mt-2"
                                  onClick={(e) => {
                                    e.preventDefault(); // Prevent page refresh
                                    e.stopPropagation(); // Prevent event propagation to the accordion
                                    setSelectedFlightId(flight.responseId); // Store flight ID in state
                                    setIsReportModalOpen(true);
                                  }}
                                >
                                  <i className="st-enable text-lightgray-150 text-md"></i>
                                  <p className="text-lightgray-200 text-md">Report Inaccurate Load</p>
                                </a>
                              </li>
                            )}
                            {flight.status === "Pending" && (
                              <li>
                                <a
                                  href="#"
                                  className="flex items-center gap-2 py-2 mt-2"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();

                                    if (!flight) return;
                                    const {
                                      airlineName,
                                      arrival,
                                      departure,
                                      sourceCode,
                                      destinationCode,
                                      flightNo,
                                      id,
                                      requestDetailId,
                                      airlineId,
                                    } = flight;
                                    const baseUrl = "https://standbytravel.vofoxsolutions.com"; // Change this to your actual base URL
                                    const url = `${baseUrl}/share-request?airlineName=${encodeURIComponent(airlineName)}&arrival=${encodeURIComponent(arrival)}&departure=${encodeURIComponent(departure)}&sourceCode=${encodeURIComponent(sourceCode)}&destinationCode=${encodeURIComponent(destinationCode)}&flightNo=${encodeURIComponent(flightNo)}&id=${encodeURIComponent(id)}&requestDetailId=${encodeURIComponent(requestDetailId)}&airlineId=${encodeURIComponent(airlineId)}`;
                                    setShareUrl(url);
                                    setShareRequestModalOpen(true);
                                  }}
                                >
                                  <i className="st-share text-lightgray-150 text-md"></i>
                                  <p className="text-lightgray-200 text-md">Share Request</p>
                                </a>
                              </li>
                            )}
                            {flight.isEnabledStatus === false && flight.status === "Answered" && (
                              <li>
                                <a
                                  href="#"
                                  className="flex items-center gap-2 py-2 mt-2"
                                  onClick={(e) => {
                                    e.preventDefault(); // Prevents page refresh
                                    e.stopPropagation(); // Prevents event propagation to the accordion
                                    // Set the current flight before opening the modal
                                    handleSingleStatusUpdate(flight);
                                  }}
                                >
                                  <i className="st-warning text-lightgray-150 text-md"></i>
                                  <p className="text-lightgray-200 text-md">Enable Status Update</p>
                                </a>
                              </li>
                            )}
                            <li>
                              <a
                                href="#"
                                className="flex items-center gap-2 py-2 mt-2"
                                onClick={(e) => {
                                  e.preventDefault(); // Prevents page refresh
                                  e.stopPropagation(); // Prevents event propagation to the accordion
                                  // Set the current flight before opening the modal
                                  handleSingleDelete(flight);
                                }}
                              >
                                <i className="st-delete text-lightgray-150 text-md"></i>
                                <p className="text-lightgray-200 text-md">Delete Request</p>
                              </a>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              </div>

              {/* Add this new condition to show the "Answered" accordion */}
              {openAccordion.has(flight.id) && (flight?.status === "Answered" || flight?.status === "Update Requested") && (
                <div className="bg-white rounded-b-lg mt-3">
                  <div className="p-4 border-t border-gray-300">
                    <div className="grid grid-cols-12 gap-6 items-end justify-between">
                      <div className="flex flex-col gap-1 lg:col-span-2 md:col-span-8 col-span-12">
                        <p className="text-lightgray-200 text-xs">Answered By</p>
                        <p className="text-grease-100 text-sm font-medium">{flight?.responderName || "Unknown"}</p>
                        <div className="flex items-center gap-1 text-xs text-lightgray-150">
                          <i className="st-time"></i>
                          <p>{getTimeAgo(flight?.modifiedDate)}</p>
                        </div>
                      </div>
                      <div className="flex md:flex-nowrap flex-wrap items-center lg:col-span-5 lg:justify-center col-span-12 gap-6">
                        <div className="flex flex-col gap-2">
                          <p className="text-lightgray-200 text-xs">Open Seats</p>
                          <div className="flex items-center gap-2">
                            <div className="flex flex-col items-center border border-lightgray-100 py-1 w-12 rounded-md">
                              <p className="text-grease-100 text-sm font-medium">{flight?.firstClass || 0}</p>
                              <p className="text-lightgray-150 text-xs">First</p>
                            </div>
                            <div className="flex flex-col items-center border border-lightgray-100 py-1 w-12 rounded-md">
                              <p className="text-grease-100 text-sm font-medium">{flight?.busClass || 0}</p>
                              <p className="text-lightgray-150 text-xs">Bus</p>
                            </div>
                            <div className="flex flex-col items-center border border-lightgray-100 py-1 w-12 rounded-md">
                              <p className="text-grease-100 text-sm font-medium">{flight?.ecoPlus || 0}</p>
                              <p className="text-lightgray-150 text-xs">Eco+</p>
                            </div>
                            <div className="flex flex-col items-center border border-lightgray-100 py-1 w-12 rounded-md">
                              <p className="text-grease-100 text-sm font-medium">{flight?.eco || 0}</p>
                              <p className="text-lightgray-150 text-xs">Eco</p>
                            </div>
                            <div className="flex flex-col items-center bg-lightgray-100 bg-opacity-40 py-1 w-12 rounded-md">
                              <p className="text-grease-100 text-sm font-medium">{flight?.total || 0}</p>
                              <p className="text-lightgray-150 text-xs">Total</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <p className="text-lightgray-200 text-xs">Listed Non-Rev Passengers</p>
                          <div className="flex flex-col items-center bg-lightgray-100 bg-opacity-40 py-1 w-full rounded-md">
                            <p className="text-grease-100 text-sm font-medium">{flight?.nonRevListed || 0}</p>
                            <p className="text-lightgray-150 text-xs">Total</p>
                          </div>
                        </div>
                      </div>
                      <div className="lg:col-span-5 md:col-span-12 col-span-12 gap-3">
                        <div className="flex items-center justify-between gap-4 border p-3 border-lightgray-100 rounded-md">
                          <p className="text-grease-100 text-sm font-medium">{flight?.flightNo}</p>
                          <p className="text-white bg-green-500 rounded-sm px-1 py-0.5 text-xs uppercase">Scheduled</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))
      )}

      {isReportModalOpen && (
        <ReportInaccurateLoadPopup loadResponseId={selectedFlightId ?? 0} onClose={() => setIsReportModalOpen(false)} />
      )}

      {/* Request Update Modal - Now handling both single and bulk operations */}
      <RequestUpdateModal
        isOpen={isRequestUpdateOpen}
        onClose={() => {
          setIsRequestUpdateOpen(false);
          setOpenDropdown(null);
        }}
        flightNo={currentFlight?.flightNo}
        createdDate={currentFlight?.createdDate}
        isBulkOperation={isBulkOperation}
        requestIds={isBulkOperation ? getSelectedRequestIds() : currentFlight ? [currentFlight?.id] : []}
      />

      <DeleteRequestModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setOpenDropdown(null);
        }}
        flightNo={currentFlight?.flightNo}
        createdDate={currentFlight?.createdDate}
        departureDate={currentFlight?.departure}
        isBulkOperation={isBulkOperation}
        // requestIds={
        //   isBulkOperation
        //     ? getSelectedRequestIds()
        //     : currentFlight
        //       ? [currentFlight?.requestDetailId]
        //       : []
        // }
        requestIds={
          isBulkOperation
            ? flights?.items
                ?.filter((flight: any) => getSelectedRequestIds().includes(flight.id))
                ?.map((flight: any) => flight.requestDetailId)
                ?.filter(Boolean)
            : currentFlight
              ? [currentFlight?.requestDetailId]
              : []
        }
        onDeleteSuccess={fetchLoadRequests}
      />
      {flights.items.length > 0 && (
        <div className="flex justify-center sticky bottom-6 z-40 mt-6">
          <button
            className={`p-4 w-64 rounded-lg text-white text-sm transition-all ${
              selectedFlights.length > 0 ? "bg-[#FFBC00] shadow-button hover:scale-105" : "bg-gray-400 cursor-not-allowed"
            }`}
            onClick={openPostModal}
            disabled={selectedFlights.length === 0}
          >
            POST REQUEST
          </button>
        </div>
      )}

      {/* Share Request Modal */}
      <ShareRequestModal isOpen={isShareRequestModalOpen} onClose={() => setShareRequestModalOpen(false)} shareUrl={shareUrl} />

      {/* Post Request Modal */}
      {isPostModalOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white rounded-xl p-6 shadow-2xl w-full max-w-md mx-4" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-semibold">Post Request</h3>
              <button aria-label="Close" onClick={() => setIsPostModalOpen(false)} className="text-lightgray-150">
                ✕
              </button>
            </div>
            <div className="my-4 ">
              <label className="text-xs text-lightgray-200 block mb-2">Date</label>
              <div className="relative">
                <DatePicker
                  selected={postDate ? new Date(postDate) : null}
                  onChange={(date: Date | null) => setPostDate(date ? date.toISOString().slice(0, 10) : "")}
                  minDate={new Date()}
                  //   onCalendarOpen={() => {
                  //     /* noop */
                  //   }}
                  //   onCalendarClose={() => {
                  //     /* noop */
                  //   }}
                  className="w-full p-2 border border-gray-200 rounded"
                  wrapperClassName="w-full"
                  placeholderText="Select departure date"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setIsPostModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded text-sm">
                Cancel
              </button>
              <button
                onClick={async () => {
                  const selectedFavIds = selectedFlights
                    .map((id) => (flights.items.find((f) => f.id === Number(id)) as any)?.id)
                    .filter((id): id is number => typeof id === "number");

                  try {
                    // ✅ Validate favourites first
                    setPostRequestLoader(true)
                    if (selectedFavIds.length > 0) {
                      await Promise.all(selectedFavIds.map((fid) => validateFavouriteFlight(fid, postDate)));

                      useToastStore.getState().showToast("Flight request posted successfully", "success", 3000);
                    }

                    // ✅ Proceed with post
                    // await performPostRequest(selectedFavIds, postDate);

                    // ✅ Close modal on success
                    setIsPostModalOpen(false);
                  } catch (error: any) {
                    console.error("Validation failed:", error);

                    const errorMessage = error?.response?.data?.message || error?.message || "Favourite validation failed";

                    useToastStore.getState().showToast(errorMessage, "error", 4000);
                  } finally {
                    setPostRequestLoader(false);
                  }
                }}
                className="px-4 py-2 bg-[#FFBC00] rounded text-sm text-white"
                disabled={selectedFlights.length === 0}
              >
                {postRequestLoader ? "Confirming..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      <AirlineNotesModal isOpen={isNotesModalOpen} onClose={handleCloseNotes} airlineNote={selectedAirlineNote} />
    </section>
  );
};

export default FavouriteListing;
